import { initializeChakraComponent } from 'Shared/chakra-initialize';
import {
   FooterNavigationList,
   FooterNavigationSection,
   FooterCopyright,
   FooterLegalLink,
   FooterLegalLinkList,
   FooterLegalSection,
   FooterSettings,
   FooterSettingsSection,
   StoreMenuButton,
   StoreMenuItem,
   Footer,
   FooterLink,
   FooterDivider,
   NavigationColumn,
   FooterTopSection,
   NavSectionProps,
   SocialMediaSection,
   NavigationAppStoreButtons,
   TRUSTED_SHOPS_BADGE_FLAG,
   TrustedShopsBadge,
   isTrustedShopsStore,
} from '@ifixit/footer';
import { EventTracker } from '@ifixit/tracking-hooks';
import { getGuideFooterMenus, getNewsletterForm } from 'Guide/guideData';
import { Menu, MenuList, Text, Stack, TextProps, Link } from '@chakra-ui/react';
import { Flag, FlagCountryCode, Language } from '@ifixit/icons';
import { StoreListItem } from 'Guide/store';
import { Menu as MenuType, MenuItemTracking } from 'Guide/menu';
import { NewsletterComponent } from 'Guide/newsletter';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { hasUserConsentedFromCookie } from '@ifixit/consent-banner/consent-storage';
import { SmartLink } from 'Shared/smart_link';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { useTranslations } from '@ifixit/i18n/hooks';
import type { TranslationFunction } from '@ifixit/i18n/types';

function FooterComponent({
   privacyPolicyUrl,
   batteryPolicyUrl,
   imprintData,
   storeCode,
   needsConsent,
   piwikSiteId,
}: {
   privacyPolicyUrl: string;
   batteryPolicyUrl?: string | null;
   imprintData?: { label: string; url: string } | null;
   storeCode: string;
   needsConsent: boolean;
   piwikSiteId: string;
}) {
   const { menu1, menu2, menu3, bottomMenu } = getGuideFooterMenus(
      privacyPolicyUrl,
      batteryPolicyUrl,
      imprintData
   );
   const newsletterForm = getNewsletterForm();
   const t = useTranslations('Footer');

   return (
      <EventTracker value={{ trackClick: trackAnalyticsCustomEvent }}>
         <Footer mt={10} pt={12} pb={6}>
            <NavigationSection
               menu1={menu1}
               menu2={menu2}
               menu3={menu3}
               newsletterForm={newsletterForm}
               newsletterLinkMessageSubscribed="Let me read it!"
               newsletterLinkMessageUnsubscribed="Let me read it first!"
               updateConsentMessage={t('updateCookieMessage')}
               LinkComponent={SmartLink}
            />
            <FooterDivider my={2} />
            <SettingsSection t={t} />
            <FooterDivider my={2} />
            <BottomLogo t={t} />
            <TrustedShopsFooterBadge
               storeCode={storeCode}
               needsConsent={needsConsent}
               piwikSiteId={piwikSiteId}
            />
            <LegalSection bottomMenu={bottomMenu} />
         </Footer>
      </EventTracker>
   );
}

const FooterText = (props: TextProps) => (
   <Text
      m={0}
      p={0}
      color="gray.300"
      _hover={{ color: 'white', textDecoration: 'none' }}
      {...props}
   />
);

const StoreMenu = ({
   stores,
   t,
}: {
   stores: StoreListItem[] | undefined;
   t: TranslationFunction;
}) => {
   if (!stores || stores.length === 0) {
      return null;
   }
   return (
      <Menu isLazy lazyBehavior="keepMounted">
         <StoreMenuButton icon={<Flag code={FlagCountryCode.US} />}>
            <FooterText>{t('Region')}</FooterText>
         </StoreMenuButton>
         <StoreMenuItems stores={stores} />
      </Menu>
   );
};

const StoreMenuItems = ({ stores }: { stores: StoreListItem[] }) => {
   const storeMenuItems = stores.map(store => (
      <StoreMenuItem
         key={store.code}
         as="a"
         href={store.url}
         icon={<Flag code={store.code.toUpperCase() as any} />}
         name={store.name}
         currency={store.currency}
      />
   ));
   return <MenuList>{storeMenuItems}</MenuList>;
};

type TrustedShopsFooterBadgeProps = {
   storeCode: string;
   needsConsent: boolean;
   piwikSiteId: string;
};

const TrustedShopsFooterBadge = ({
   storeCode,
   needsConsent,
   piwikSiteId,
}: TrustedShopsFooterBadgeProps) => {
   const growthbook = useGrowthBook();

   if (!isTrustedShopsStore(storeCode)) return null;

   const consentGranted = hasUserConsentedFromCookie(piwikSiteId) ?? !needsConsent;
   if (!consentGranted) return null;
   if (!growthbook?.isOn(TRUSTED_SHOPS_BADGE_FLAG)) return null;

   return <TrustedShopsBadge storeCode={storeCode} />;
};

type SettingsSectionProps = {
   stores?: StoreListItem[];
};

const SettingsSection = ({ stores, t }: SettingsSectionProps & { t: TranslationFunction }) => (
   <FooterSettingsSection>
      <FooterSettings>
         <StoreMenu stores={stores} t={t} />
         <TranslateLink t={t} />
      </FooterSettings>
      <SocialMediaSection />
   </FooterSettingsSection>
);

const TranslateLink = ({ t }: { t: TranslationFunction }) => (
   <FooterLink
      href="/Translate"
      icon={Language}
      fontSize="md"
      color="gray.300"
      _hover={{ color: 'white' }}
   >
      {t('helpTranslate')}
   </FooterLink>
);

type NavigationSectionProps = Omit<NavSectionProps, 'storeCode'>;
const NavigationSection = ({
   menu1,
   menu2,
   menu3,
   newsletterForm,
   updateConsentMessage,
   LinkComponent,
}: NavigationSectionProps) => (
   <FooterTopSection>
      <FooterNavigationSection>
         <FooterNavigationList>
            <NavigationColumn
               menu={menu1}
               listFontSize="md"
               fontSize="lg"
               fontWeight="bold"
               LinkComponent={LinkComponent}
            />
         </FooterNavigationList>
         <FooterNavigationList>
            <NavigationColumn
               menu={menu2}
               listFontSize="md"
               fontSize="lg"
               fontWeight="bold"
               LinkComponent={LinkComponent}
            />
         </FooterNavigationList>
         <FooterNavigationList>
            <NavigationColumn
               menu={menu3}
               listFontSize="md"
               fontSize="lg"
               fontWeight="bold"
               updateConsentMessage={updateConsentMessage}
               LinkComponent={LinkComponent}
            />
         </FooterNavigationList>

         <NavigationAppStoreButtons fontSize="lg" fontWeight="bold" LinkComponent={LinkComponent} />
      </FooterNavigationSection>
      {newsletterForm && <NewsletterComponent newsletterForm={newsletterForm} />}
   </FooterTopSection>
);

const BottomLogo = ({ t }: { t: TranslationFunction }) => (
   <Link
      href="/Manifesto"
      color="gray.300"
      fontSize="md"
      fontWeight="normal"
      _visited={{ color: 'gray.300' }}
      _hover={{ textDecoration: 'none' }}
   >
      <Stack direction="column" spacing={3} pt={5} align="center">
         <RepairFistSVG />
         <div>{t('repair')}</div>
      </Stack>
   </Link>
);

const LegalSection = ({ bottomMenu }: { bottomMenu: MenuType | null }) => (
   <FooterLegalSection pt={4}>
      <FooterCopyright copyrightName="iFixit" locale="en-us" />
      <FooterLegalLinkList>
         <LegalLinks bottomMenu={bottomMenu} />
      </FooterLegalLinkList>
   </FooterLegalSection>
);

const LegalLinks = ({ bottomMenu }: { bottomMenu: MenuType | null }) => {
   if (!bottomMenu || !bottomMenu.items) {
      return null;
   }
   const links = bottomMenu.items.map((item: MenuItemTracking, index: number) => {
      if (item.type !== 'link') {
         return null;
      }
      return (
         <FooterLegalLink key={index} href={item.url}>
            <FooterText color="gray.400" fontSize="sm">
               {item.name}
            </FooterText>
         </FooterLegalLink>
      );
   });
   return <>{links}</>;
};

const RepairFistSVG = () => (
   <svg fill="none" height="80" viewBox="0 0 74 80" width="74" xmlns="http://www.w3.org/2000/svg">
      <path
         d="m34.9624 33.244c.5147-.3861.5576-1.0724.1716-1.5443l-4.0322-4.6327c-.4289-.4718-1.1582-.5147-1.63-.1287l-4.7614 3.9035c-.4719.3861-.5148 1.0724-.0858 1.5443l6.7346 6.9919c.4289.429.9008.6863 1.0724.5148.1287-.1716.0429-.7293-.2145-1.3298l-.6863-1.4584c-.2574-.5577-.0429-1.3298.4289-1.7159zm-16.9008 9.5228-2.8311 3.26c-.429.4719-.3432 1.1582.1287 1.5442l9.3512 7.3781c.4718.386 1.244.3002 1.63-.1716l2.5308-2.9598c.3861-.4719.3432-1.2011-.1286-1.5871l-9.0939-7.6354c-.4718-.3432-1.201-.3003-1.5871.1716zm5.6193-7.7641-3.8606 4.4611c-.4289.4719-.3432 1.2011.1287 1.5871l9.6944 8.4933c.4718.429 1.2439.429 1.7587.0429l3.8177-3.0884c.4718-.3861.5147-1.0724.0858-1.5014l-10.0805-10.0375c-.4289-.429-1.1152-.429-1.5442.0429zm-11.3673 28.9973c-.2145-.9866-.9008-1.7158-1.7158-2.1019-.5577-.2573-1.20109-.3002-1.84452-.1716-.9008.2145-1.63002.7722-2.01608 1.5443-.30027.6005-.42896 1.2868-.25738 2.0161.17159.8579.72923 1.5013 1.41555 1.9303.64344.386 1.41555.5147 2.18763.3431.7722-.1716 1.4585-.6434 1.8445-1.2868.3861-.6435.5577-1.4585.3861-2.2735zm.1716 4.3753s-.815.6864-1.5442.9008c-.98664.3432-2.05903.3432-3.08852.0429-.68633-.2144-1.32976-.6005-1.8874-1.1152-.77212-.7293-1.24397-1.6301-1.45845-2.5738-.12868-.5576-.17158-1.1153-.08579-1.7158.08579-.5147.21448-1.0724.47185-1.5442.21448-.3861.42896-.6864.42896-.6864.21448-.2573.51474-.6434.77212-.8579l10.03753-10.3378c.2145-.2144.6005-.2573.8579-.0429l6.563 5.4478c.2574.2144.2574.5576.0429.7721l-10.2949 10.9383c-.2145.2145-.6005.5577-.815.7721zm26.1662-23.0777 3.5174-2.5308c.5148-.3432.429-.6864-.2144-.6864l-6.9491-.0858c-.6434 0-.7721.3432-.3432.8151l2.2735 2.3592c.4289.429 1.2011.5147 1.7158.1287zm4.5898-21.8767 1.3727 1.7158c.2145.2574.6005.3861.9008.3432l.2574-.0429c.3002-.0429.7292.0858.9436.3003l3.0456 3.1742c.2145.2145.5577.2145.7721 0l2.5738-2.6595c.2144-.2145.6434-.5147.9437-.6005l12.1823-4.3325c.3002-.0858.3431-.386.1287-.6005l-10.2521-10.76676c-.2144-.21447-.386-.68633-.3431-.98659l.0858-1.28687c0-.30026.2145-.72922.4289-.9437l5.8338-5.53351c.2145-.214475.2574-.557638 0-.815011-.2145-.214477-.6434-.386059-.9437-.386059l-6.4772.557641c-.3003.042895-.7721.214477-.9866.428954l-5.8338 5.576405c-.2144.21448-.4289.64343-.4289.9437l-.4719 10.6381c0 .3002-.2145.7721-.4289.9866l-3.2601 3.4316c-.2145.2574-.2574.6434-.0429.8579zm18.3593-10.5523c-.3003 0-.3861.1716-.1716.3861l6.0053 6.3914c.2145.2145.4719.1716.5577-.1287l1.244-3.5603c.0857-.3003.1715-.7721.1715-1.1153l-.1715-5.96244c0-.30027-.1716-.77212-.429-.9866l-.0429-.04289c-.2145-.21448-.5576-.21448-.815 0l-4.933 4.67563c-.2145.2144-.6863.386-.9866.386zm-23.8499 18.4022 5.0617-3.6461c.5147-.3432.6005-1.0724.2144-1.5443l-4.2466-5.319c-.3861-.4719-1.1153-.5576-1.5871-.1716l-4.9759 4.0751c-.4719.386-.5576 1.1153-.1287 1.5871l4.0322 4.7614c.386.5148 1.1582.6005 1.63.2574zm-31.57104 12.0965c0-16.9866 13.81234-30.8418 30.84184-30.8418 2.874 0 5.7051.386 8.3646 1.1581l.2574-5.91953c-2.7453-.68632-5.6622-1.02949-8.622-1.02949-20.2038 0-36.632728 16.42892-36.632728 36.63272 0 5.9625 1.415548 11.5818 3.946378 16.5576l4.33244-4.4182c-1.58713-3.7319-2.48793-7.8499-2.48793-12.1394zm67.43164 0c0 20.2037-16.429 36.6327-36.6327 36.6327-9.3083 0-17.8016-3.4745-24.2359-9.2225l3.9892-4.2038c3.9893 3.4746 8.8794 5.9625 14.2842 7.0349l-.5576-3.9035c-.0858-.6005-.5577-1.4584-1.0295-1.8445l-3.5174-2.9598c-.4719-.3861-1.1153-1.2011-1.3727-1.7587l-1.3726-2.7882c-.2574-.5577-.1716-1.4156.2573-1.8874l.6006-.6863c.4289-.4719 1.0295-.8151 1.3726-.7293.3432.0858.9437-.2573 1.3727-.7292l4.1608-4.8472c.429-.4718 1.2011-1.0724 1.7587-1.3297l6.3915-3.0027c.5576-.2574.8579 0 .6434.6005l-5.6193 16c-.2145.6006.0858 1.1582.6863 1.244.6006.0858 1.244.0858 1.4585 0 .2144-.0858.4718.3003.6005.9008l1.7158 8.2359c.815-.0429 1.5871-.1287 2.3593-.2144l-2.4451-8.622c-.1716-.6006.1716-1.244.7721-1.4585l.2574-.0858c.6006-.2144.6006-.5576.0429-.7721l-2.5737-1.0295c-.5577-.2573-.8579-.9008-.6434-1.5013l5.6193-14.4129c.2144-.6005.815-1.3726 1.3297-1.7158l2.1877-1.63c.5147-.3861 1.4155-.815 2.0161-.9866l.3002-.0858c.6006-.1716.6006-.3861-.0429-.5148l-1.1152-.2144c-.6006-.1287-1.2869-.4719-1.4585-.7722-.1716-.3002-.3003-1.0723-.2574-1.6729l.0858-1.0295c.0429-.6434-.2144-.7292-.6005-.2145l-1.8874 2.5738c-.3861.5147-1.2011.9008-1.8016.9437l-5.1904.0429c-.6434 0-1.3297-.4719-1.5442-1.0295l-.9437-2.2735c-.2574-.5576 0-1.3297.5147-1.7158l11.1958-7.9357c.5147-.3431 1.2868-.3002 1.7587.1287l11.3244 11.0671c.4289.4289.6434 1.2868.386 1.8445l-8.2359 20.9329c-.2145.6006-.2145 1.5443.0429 2.1019l2.6595 6.0054c9.0081-5.4048 15.0563-15.2279 15.0563-26.4665 0-6.9062-2.2734-13.2976-6.134-18.4451l5.6622-2.016c4.0321 5.7908 6.3485 12.8257 6.3485 20.4182z"
         fill="#fff"
      />
   </svg>
);

initializeChakraComponent('Footer', FooterComponent);
